import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import { UpgradeModal } from '../../components/SubscriptionComponents';


// ========== LIETUVIŲ KALBOS MOKYMOSI CENTRAS ==========
const LithuanianLanguageCenter = () => {
    const [selectedGame, setSelectedGame] = useState(null);
    const [gameData, setGameData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [score, setScore] = useState(0);
    const navigate = useNavigate();

    // Prijungiame reikiamus hook'us
    const { user } = useAuth();
    const { checkLimit, updateUsage } = useSubscription();

    const games = [
        { id: 'alphabet', name: 'Abėcėlė', icon: '🔤', color: 'from-blue-500 to-purple-500', description: 'Mokykis lietuviškų raidžių', emoji: 'ĄČĘĖĮŠŲŪŽ' },
        { id: 'syllables', name: 'Skiemenys', icon: '✂️', color: 'from-green-500 to-teal-500', description: 'Skaičiuok skiemenis', emoji: 'KA-TI-NAS' },
        { id: 'word-formation', name: 'Žodžių sudarymas', icon: '🔡', color: 'from-pink-500 to-red-500', description: 'Sudėk raides į žodžius', emoji: '📝✨' },
        { id: 'grammar', name: 'Gramatika', icon: '📚', color: 'from-orange-500 to-yellow-500', description: 'Lietuvių kalbos taisyklės', emoji: 'KAS?KĄ?' },
        { id: 'reading', name: 'Skaitymas', icon: '📖', color: 'from-indigo-500 to-blue-500', description: 'Skaityk pasakas', emoji: '🐰📚' },
        { id: 'rhyming', name: 'Rimai', icon: '🎵', color: 'from-purple-500 to-pink-500', description: 'Rask rimuojančius žodžius', emoji: '🐱➡️🐦' },
        { id: 'singular-plural', name: 'Skaičiai', icon: '1️⃣2️⃣', color: 'from-teal-500 to-green-500', description: 'Vienaskaita ir daugiskaita', emoji: '🐱➡️🐱🐱' },
        { id: 'antonyms', name: 'Priešingumai', icon: '↔️', color: 'from-red-500 to-orange-500', description: 'Priešingos reikšmės', emoji: '🐘↔️🐁' }
    ];

    // ==========================================================
    // PATAISYTA `generateGame` FUNKCIJA SU API KVIETIMU
    // ==========================================================
    const generateGame = async (gameType) => {
        // 1. Tikriname limitus
        if (!checkLimit()) {
            setShowUpgradeModal(true);
            return;
        }

        setLoading(true);
        setSelectedGame(games.find(g => g.id === gameType));
        setGameData(null); // Išvalome senus duomenis

        try {
            const token = localStorage.getItem('access_token');

            // 2. API endpoint'ų ir parametrų konfigūracija
            const gameApiConfig = {
                'alphabet': { endpoint: '/api/v1/language/alphabet', params: { difficulty: 'easy' } },
                'syllables': { endpoint: '/api/v1/language/syllables', params: { difficulty: 'easy', word_count: 8 } },
                'word-formation': { endpoint: '/api/v1/language/word-formation', params: { difficulty: 'easy', word_count: 5 } },
                'grammar': { endpoint: '/api/v1/language/grammar', params: { difficulty: 'easy', topic: 'daiktavardžiai' } },
                'reading': { endpoint: '/api/v1/language/reading-comprehension', params: { difficulty: 'easy', story_count: 1 } },
                'rhyming': { endpoint: '/api/v1/language/rhyming', params: { difficulty: 'easy', pair_count: 6 } },
                'singular-plural': { endpoint: '/api/v1/language/singular-plural', params: { difficulty: 'easy', word_count: 8 } },
                'antonyms': { endpoint: '/api/v1/language/antonyms', params: { difficulty: 'easy', pair_count: 8 } }
            };

            const config = gameApiConfig[gameType];
            if (!config) throw new Error("Neteisingas žaidimo tipas");

            // 3. API kvietimas
            const response = await fetch(`${process.env.REACT_APP_API_URL}${config.endpoint}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ ...config.params, age: user?.age, grade: user?.grade })
            });

            if (!response.ok) {
                if (response.status === 429) {
                    alert('AI limitai išnaudoti! Pagerink prenumeratą! 🚀');
                } else {
                    throw new Error('Nepavyko sugeneruoti žaidimo iš serverio');
                }
                setLoading(false);
                return;
            }

            const data = await response.json();

            // Update usage stats immediately to reflect token consumption
            if (data.usage_stats) {
                updateUsage(data.usage_stats);
            }
            
            setGameData(data.game_data);
            setScore(0);

        } catch (error) {
            console.error('Failed to generate game:', error);
            alert('Klaida generuojant žaidimą. Bandykite dar kartą.');
            setSelectedGame(null); // Grįžtame į pradinį langą
        } finally {
            setLoading(false);
        }
    };


    const renderGameContent = () => {
        if (!gameData) return <div>Žaidimas kuriamas...</div>;

        switch (gameData.game_type) {
            case 'alphabet_learning':
                return <AlphabetGame data={gameData} onScore={setScore} />;
            case 'syllables_counting':
                return <SyllablesGame data={gameData} onScore={setScore} />;
            case 'word_formation':
                return <WordFormationGame data={gameData} onScore={setScore} />;
            case 'reading_comprehension':
                return <ReadingGame data={gameData} onScore={setScore} />;
            case 'rhyming_words':
                return <RhymingGame data={gameData} onScore={setScore} />;
            case 'singular_plural':
                return <SingularPluralGame data={gameData} onScore={setScore} />;
            case 'antonyms':
                return <AntonymsGame data={gameData} onScore={setScore} />;
            case 'grammar_learning':
                return <GrammarGame data={gameData} onScore={setScore} />; // Pridedame trūkstamą gramatikos komponentą
            default:
                return <div>Neatpažintas žaidimo tipas: {gameData.game_type}</div>;
        }
    };

    // Game view
    if (selectedGame && !loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 p-8">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                        <div className="flex justify-between items-center">
                            <button
                                onClick={() => { setSelectedGame(null); setGameData(null); }}
                                className="px-6 py-3 bg-gray-200 rounded-xl hover:bg-gray-300 transition font-bold"
                            >
                                ← Atgal
                            </button>

                            <div className="text-center">
                                <h2 className="text-2xl font-bold text-gray-800 flex items-center justify-center gap-2">
                                    <span className="text-3xl">{selectedGame.icon}</span>
                                    {gameData ? gameData.title : selectedGame.name}
                                </h2>
                            </div>

                            <div className="text-right">
                                <p className="text-sm text-gray-600">Taškai</p>
                                <p className="text-3xl font-bold text-purple-600">⭐ {score}</p>
                            </div>
                        </div>
                    </div>

                    {renderGameContent()}
                </div>
            </div>
        );
    }

    // Loading
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-8xl mb-6 animate-bounce">🇱🇹</div>
                    <h2 className="text-3xl font-bold text-gray-800">AI generuoja užduotį...</h2>
                </div>
            </div>
        );
    }

    // Games selection
    return (
        <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 p-8">
            <div className="max-w-7xl mx-auto">
                <div className="relative mb-12 text-center">
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="absolute left-0 top-0 px-6 py-3 bg-white text-gray-700 rounded-xl hover:bg-gray-100 transition font-bold shadow-lg flex items-center gap-2 border border-gray-200"
                    >
                        ← Į pradžią
                    </button>
                    <div className="text-8xl mb-4">🇱🇹</div>
                    <h1 className="text-5xl font-bold text-gray-800 mb-4">Lietuvių Kalba</h1>
                    <p className="text-xl text-gray-600">Mokykis lietuvių kalbos žaisdamas!</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                    {games.map((game) => (
                        <div
                            key={game.id}
                            onClick={() => generateGame(game.id)}
                            className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition transform hover:scale-105 cursor-pointer"
                        >
                            <div className={`bg-gradient-to-r ${game.color} p-8 text-white text-center`}>
                                <div className="text-8xl mb-4">{game.icon}</div>
                                <h3 className="text-2xl font-bold mb-2">{game.name}</h3>
                            </div>
                            <div className="p-6">
                                <p className="text-gray-600 text-center mb-3 h-12">{game.description}</p>
                                <div className="text-center text-3xl mb-4">{game.emoji}</div>
                                <button className={`w-full py-3 bg-gradient-to-r ${game.color} text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition`}>Žaisti</button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="bg-white rounded-3xl shadow-2xl p-12">
                    <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">🎓 Ko išmoksi?</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="text-center"><div className="text-6xl mb-3">🔤</div><h3 className="text-xl font-bold text-gray-800 mb-2">Abėcėlė</h3><p className="text-gray-600">33 lietuviškos raidės</p></div>
                        <div className="text-center"><div className="text-6xl mb-3">✂️</div><h3 className="text-xl font-bold text-gray-800 mb-2">Skiemenys</h3><p className="text-gray-600">Dalyk žodžius</p></div>
                        <div className="text-center"><div className="text-6xl mb-3">📚</div><h3 className="text-xl font-bold text-gray-800 mb-2">Gramatika</h3><p className="text-gray-600">Kalbos taisyklės</p></div>
                        <div className="text-center"><div className="text-6xl mb-3">📖</div><h3 className="text-xl font-bold text-gray-800 mb-2">Skaitymas</h3><p className="text-gray-600">Lietuviškos pasakos</p></div>
                    </div>
                </div>
            </div>
            <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
        </div>
    );
};


// ========== MINI GAME COMPONENTS (lieka nepakitę, bet pridedu trūkstamą GrammarGame) ==========

const GrammarGame = ({ data, onScore }) => {
    const [currentExercise, setCurrentExercise] = useState(0);

    const handleAnswer = (option) => {
        if (option === data.exercises[currentExercise].correct_answer) {
            onScore(s => s + (data.exercises[currentExercise].points || 10));
            if (currentExercise + 1 < data.exercises.length) {
                setCurrentExercise(currentExercise + 1);
            } else {
                alert("Gramatikos pratimas baigtas!");
            }
        } else {
            alert("Neteisingai, bandyk dar kartą!");
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">{data.topic}</h3>
            <p className="text-xl text-gray-600 mb-8">{data.exercises[currentExercise].question}</p>
            <div className="grid grid-cols-1 gap-4">
                {data.exercises[currentExercise].options.map((option, index) => (
                    <button
                        key={index}
                        onClick={() => handleAnswer(option)}
                        className="p-4 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-xl font-bold text-lg hover:shadow-xl transition"
                    >
                        {option}
                    </button>
                ))}
            </div>
        </div>
    );
};

const AlphabetGame = ({ data, onScore }) => {
    const [currentLetter, setCurrentLetter] = useState(0);
    return (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-9xl mb-6">{data.letters[currentLetter].emoji}</div>
            <div className="text-8xl font-bold text-purple-600 mb-4">{data.letters[currentLetter].letter}</div>
            <p className="text-3xl font-bold text-gray-800 mb-2">{data.letters[currentLetter].example_word}</p>
            <p className="text-lg text-gray-600 mb-8">Garsas: {data.letters[currentLetter].sound}</p>
            <button onClick={() => setCurrentLetter((currentLetter + 1) % data.letters.length)} className="px-12 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-bold text-xl shadow-lg hover:shadow-xl transition">Kita raidė →</button>
        </div>
    );
};

const SyllablesGame = ({ data, onScore }) => {
    const [currentWord, setCurrentWord] = useState(0);
    return (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-8xl mb-6">{data.words[currentWord].emoji}</div>
            <div className="text-5xl font-bold text-gray-800 mb-4">{data.words[currentWord].syllables_separated}</div>
            <p className="text-2xl text-gray-600 mb-8">{data.words[currentWord].syllables_count} skiemenys</p>
            <button onClick={() => setCurrentWord((currentWord + 1) % data.words.length)} className="px-12 py-4 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-2xl font-bold text-xl shadow-lg">Kitas žodis →</button>
        </div>
    );
};

const WordFormationGame = ({ data, onScore }) => {
    const [currentWord, setCurrentWord] = useState(0);
    const [userWord, setUserWord] = useState([]);
    const word = data.words[currentWord];

    // Paprasta funkcija, kuri patikrina, ar raidė yra prieinama
    const isLetterAvailable = (letter, index) => {
        const totalInWord = word.scrambled_letters.filter(l => l === letter).length;
        const usedInAnswer = userWord.filter(l => l === letter).length;
        return usedInAnswer < totalInWord;
    };

    // Sukuriame prieinamų raidžių masyvą su unikaliais raktais
    const availableLetters = word.scrambled_letters
        .map((letter, index) => ({ letter, originalIndex: index }))
        .filter((item, index) => {
            // Ši logika yra sudėtinga, kad teisingai veiktų su pasikartojančiomis raidėmis.
            // Paprastesniam variantui, galima tiesiog rodyti visas raides ir neleisti paspausti panaudotų.
            const usedIndices = userWord.map(u => u.originalIndex);
            return !usedIndices.includes(index);
        });


    return (
        <div className="bg-white rounded-2xl shadow-xl p-12">
            <div className="text-center mb-8">
                <div className="text-8xl mb-4">{word.emoji}</div>
                <p className="text-2xl font-bold text-gray-800">💡 {word.hint}</p>
            </div>
            <div className="flex justify-center gap-2 mb-8 min-h-20 border-2 border-dashed p-4 rounded-lg">
                {userWord.map((item, index) => (
                    <button key={index} onClick={() => setUserWord(userWord.filter((_, i) => i !== index))} className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 text-white rounded-xl flex items-center justify-center text-3xl font-bold cursor-pointer hover:scale-110 transition">{item.letter}</button>
                ))}
            </div>
            <div className="flex flex-wrap justify-center gap-3 mb-8">
                {availableLetters.map((item, index) => (
                    <button key={item.originalIndex} onClick={() => setUserWord([...userWord, item])} className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 text-white rounded-xl text-3xl font-bold hover:scale-110 transition shadow-lg">{item.letter}</button>
                ))}
            </div>
            {userWord.map(u => u.letter).join('') === word.target_word && (
                <div className="text-center">
                    <p className="text-3xl font-bold text-green-600 mb-4">🎉 Teisingai!</p>
                    <button onClick={() => { setCurrentWord((currentWord + 1) % data.words.length); setUserWord([]); onScore(s => s + (word.points || 30)); }} className="px-12 py-4 bg-green-500 text-white rounded-2xl font-bold text-xl">Kitas žodis →</button>
                </div>
            )}
        </div>
    );
};

const ReadingGame = ({ data, onScore }) => {
    const story = data.stories[0];
    const [currentQ, setCurrentQ] = useState(0);

    const handleAnswer = (option) => {
        if (option === story.questions[currentQ].correct_answer) {
            onScore(s => s + (story.questions[currentQ].points || 15));
            if (currentQ + 1 < story.questions.length) {
                setCurrentQ(currentQ + 1);
            } else {
                alert("Pasaka perskaityta!");
            }
        } else {
            alert("Neteisingai, bandyk dar kartą!");
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-2xl p-8"><h3 className="text-3xl font-bold text-center text-indigo-800 mb-6">📖 {story.title}</h3><p className="text-xl text-gray-800 leading-relaxed">{story.text}</p></div>
            {story.questions[currentQ] ? (
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                    <p className="text-2xl font-bold text-gray-800 mb-6 text-center">{story.questions[currentQ].question}</p>
                    <div className="grid grid-cols-1 gap-4">
                        {story.questions[currentQ].options.map((option, index) => (<button key={index} onClick={() => handleAnswer(option)} className="p-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-bold text-lg hover:shadow-xl transition">{option}</button>))}
                    </div>
                </div>
            ) : <div className="text-center text-2xl font-bold text-green-600">Sveikiname perskaičius pasaką!</div>}
        </div>
    );
};

const RhymingGame = ({ data, onScore }) => (
    <div className="bg-white rounded-2xl shadow-xl p-12">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">Rask rimuojančias poras!</h3>
        <div className="grid grid-cols-2 gap-6">
            {data.word_pairs.map((pair, index) => (<div key={index} className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-6 text-center"><div className="text-6xl mb-3">{pair.word1.emoji}</div><p className="text-2xl font-bold text-gray-800 mb-2">{pair.word1.text}</p><div className="text-4xl my-4">↕️</div><div className="text-6xl mb-3">{pair.word2.emoji}</div><p className="text-2xl font-bold text-gray-800">{pair.word2.text}</p></div>))}
        </div>
    </div>
);

const SingularPluralGame = ({ data, onScore }) => (
    <div className="bg-white rounded-2xl shadow-xl p-12">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">Vienaskaita → Daugiskaita</h3>
        <div className="space-y-6">
            {data.word_pairs.map((pair, index) => (<div key={index} className="bg-gradient-to-r from-teal-100 to-green-100 rounded-2xl p-6 flex items-center justify-around"><div className="text-center"><div className="text-6xl mb-2">{pair.emoji}</div><p className="text-2xl font-bold text-gray-800">{pair.singular}</p><p className="text-sm text-gray-600">vienaskaita</p></div><div className="text-5xl">→</div><div className="text-center"><div className="text-6xl mb-2">{pair.emoji}{pair.emoji}</div><p className="text-2xl font-bold text-gray-800">{pair.plural}</p><p className="text-sm text-gray-600">daugiskaita</p></div></div>))}
        </div>
    </div>
);

const AntonymsGame = ({ data, onScore }) => (
    <div className="bg-white rounded-2xl shadow-xl p-12">
        <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">Priešingos reikšmės</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.antonym_pairs.map((pair, index) => (<div key={index} className="bg-gradient-to-r from-red-100 to-orange-100 rounded-2xl p-6"><div className="flex items-center justify-around"><div className="text-center"><div className="text-7xl mb-2">{pair.word1.emoji}</div><p className="text-2xl font-bold text-gray-800">{pair.word1.text}</p></div><div className="text-5xl">↔️</div><div className="text-center"><div className="text-7xl mb-2">{pair.word2.emoji}</div><p className="text-2xl font-bold text-gray-800">{pair.word2.text}</p></div></div></div>))}
        </div>
    </div>
);

export default LithuanianLanguageCenter;